import { Outlet, Navigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { 
  LayoutDashboard, 
  Calendar, 
  Ticket, 
  ShoppingCart, 
  Image, 
  Users, 
  FileText, 
  MessageSquare,
  LogOut,
  Menu,
  X,
  ImagePlus,
  Share2,
  Database,
  UserPlus,
  Layout,
  BarChart3
} from "lucide-react";
import { Button } from "../components/ui/button";

import { API } from "../config/api";
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const navItems = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { path: "/admin/site-pages", label: "Site Pages", icon: Layout },
  { path: "/admin/events", label: "Events", icon: Calendar },
  { path: "/admin/tickets", label: "Tickets", icon: Ticket },
  { path: "/admin/inventory", label: "Inventory", icon: BarChart3 },
  { path: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { path: "/admin/members", label: "Members", icon: UserPlus },
  { path: "/admin/gallery", label: "Gallery", icon: Image },
  { path: "/admin/about-images", label: "About Photos", icon: ImagePlus },
  { path: "/admin/promos", label: "Social Promos", icon: Share2 },
  { path: "/admin/sponsors", label: "Sponsors", icon: Users },
  { path: "/admin/content", label: "Content", icon: FileText },
  { path: "/admin/inquiries", label: "Inquiries", icon: MessageSquare },
  { path: "/admin/backup", label: "Backup", icon: Database },
];

export default function AdminLayout() {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      setIsAuthenticated(false);
      return;
    }

    try {
      const response = await fetch(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdmin(data);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem("admin_token");
        setIsAuthenticated(false);
      }
    } catch {
      setIsAuthenticated(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setIsAuthenticated(false);
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-kerala-cream">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen flex bg-kerala-cream-alt">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 bg-kerala-green text-white
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_244098db-6247-4607-8c98-aa9a63ac3c07/artifacts/z92rohdv_WhatsApp%20Image%202026-02-09%20at%208.18.57%20PM.jpeg"
                alt="Tatvamasi Logo"
                className="w-12 h-12 object-contain bg-white rounded-lg p-1"
              />
              <div>
                <h1 className="font-heading font-semibold">Tatvamasi</h1>
                <p className="text-xs text-white/60">Admin Panel</p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = item.exact 
                ? location.pathname === item.path 
                : location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-kerala-gold text-kerala-green font-medium' 
                      : 'text-white/80 hover:bg-white/10 hover:text-white'}
                  `}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-kerala-gold/20 rounded-full flex items-center justify-center">
                <span className="text-kerala-gold font-semibold">
                  {admin?.name?.charAt(0) || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{admin?.name}</p>
                <p className="text-xs text-white/60 truncate">{admin?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-kerala-gold/10 px-4 py-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h2 className="font-heading text-xl text-kerala-green">
              {navItems.find(item => 
                item.exact 
                  ? location.pathname === item.path 
                  : location.pathname.startsWith(item.path) && !item.exact
              )?.label || 'Admin'}
            </h2>
          </div>
          <Link to="/" className="text-sm text-kerala-green hover:text-kerala-terracotta">
            View Site →
          </Link>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
